import React, { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SHORTCUT_LIST } from '../hooks/useGlobalShortcuts';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * ShortcutHelpModal — `?` tuşuna basılınca açılan kısayol listesi.
 * Sequence key'leri `g t` gibi boşluklu gösterilir.
 */
export default function ShortcutHelpModal({ open, onClose }) {
  const { t } = useLanguage();
  const ref = useRef(null);
  useFocusTrap(open, ref, { onEscape: onClose });
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcut-help-title"
    >
      <div
        ref={ref}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="shortcut-help-title" className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span aria-hidden="true">⌨️</span> {t('shortcutsTitle') || 'Klavye Kısayolları'}
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={t('close') || 'Kapat'}
          ><span aria-hidden="true">✕</span></button>
        </div>
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          <ul className="space-y-2">
            {SHORTCUT_LIST.map((s, i) => {
              // Önce primary key'i dene; çeviri yoksa (key === t(key)) fallback'e düş
              const primary = t(s.key);
              const label = (primary === s.key && s.fallbackKey) ? t(s.fallbackKey) : primary;
              return (
              <li key={i} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-700 dark:text-gray-300 flex-1">
                  {label}
                </span>
                <span className="flex gap-1 flex-shrink-0">
                  {s.keys.map((k, ki) => (
                    <kbd
                      key={ki}
                      className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-md bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 text-xs font-mono font-semibold"
                    >
                      {k}
                    </kbd>
                  ))}
                </span>
              </li>
              );
            })}
          </ul>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">
            {t('shortcutHint') || 'İpucu: bir tuşu basılı tutmadan kısa aralıklarla basın.'}
          </p>
        </div>
      </div>
    </div>
  );
}
